import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { AdvancedLifeAnalysisResult, LifeThemeId, LifeThemeQuestion, LifeThemeConfig } from '../../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../../utils/advancedLifeAnalysisTaxonomy';
import { runAdvancedLifeAnalysis } from '../../utils/advancedLifeAnalysisEngine';
import { getNatalPromiseTier, getTimingConfluence, calculateProjectedAgeWindow } from '../../utils/presentationHelper';
import { computeLifeStageContext } from '../../utils/lifeStageContextEngine';
import {
  getNativeAgeInYears,
  getDeshKaalPatraThemes,
  getDeshKaalPatraQuestions,
  getDeshKaalPatraThemeDisplay
} from '../../utils/deshKaalPatraFilter';
import {
  HelpCircle,
  Sparkles,
  Zap,
  Calendar,
  Layers,
  ChevronRight,
  ChevronLeft,
  X,
  ArrowRight,
  BookOpen,
  Briefcase,
  Heart,
  Coins,
  Home,
  Car,
  Baby,
  GraduationCap,
  Activity,
  TrendingUp,
  Globe,
  Scale,
  Users,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Compass,
  Search,
  MessageSquare,
  Award
} from 'lucide-react';

export interface BasicLifeQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  kundali: CompleteKundali;
  initialThemeId?: LifeThemeId;
  initialQuestionId?: string;
  lang: Language;
  onSelectThemeAndQuestion?: (themeId: LifeThemeId, questionId: string) => void;
  onOpenDetailedEvidence?: () => void;
  onOpenAcharya?: (question?: string) => void;
}

const THEME_ICONS: Record<LifeThemeId, React.ComponentType<{ className?: string }>> = {
  career_job: Briefcase,
  marriage_relationship: Heart,
  wealth_finance: Coins,
  house_property: Home,
  vehicle: Car,
  children: Baby,
  education: GraduationCap,
  health_vitality: Activity,
  business: TrendingUp,
  foreign_travel: Globe,
  litigation_competition: Scale,
  spirituality: Sparkles,
  family_parents: Users
};

export const BasicLifeQuestionsModal: React.FC<BasicLifeQuestionsModalProps> = ({
  isOpen,
  onClose,
  kundali,
  initialThemeId = 'career_job',
  initialQuestionId,
  lang,
  onSelectThemeAndQuestion,
  onOpenDetailedEvidence,
  onOpenAcharya
}) => {
  const isHi = lang === 'hi';
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeThemeId, setActiveThemeId] = useState<LifeThemeId>(initialThemeId);
  const [activeQuestionId, setActiveQuestionId] = useState<string>(
    initialQuestionId || LIFE_THEMES_REGISTRY[initialThemeId]?.questions[0]?.id || 'career_timing_promotion_job_change'
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentResult, setCurrentResult] = useState<AdvancedLifeAnalysisResult | null>(null);

  // Compute native's age and Desh-Kaal-Patra context
  const lifeStageContext = useMemo(() => {
    return computeLifeStageContext(kundali);
  }, [kundali]);

  const nativeAge = useMemo(() => getNativeAgeInYears(kundali), [kundali]);

  // Filter age-inappropriate domains using Desh-Kaal-Patra rules
  const ageAppropriateThemes = useMemo(() => {
    const allThemes = Object.values(LIFE_THEMES_REGISTRY);
    return getDeshKaalPatraThemes(kundali, allThemes);
  }, [kundali]);

  // Sync with initial props when opened
  useEffect(() => {
    if (isOpen) {
      const availableThemes = ageAppropriateThemes.length > 0 ? ageAppropriateThemes : Object.values(LIFE_THEMES_REGISTRY);
      const defaultTheme = availableThemes.find((t) => t.id === initialThemeId) || availableThemes[0];
      const themeId = defaultTheme ? defaultTheme.id : 'career_job';
      
      const themeQuestions = getDeshKaalPatraQuestions(kundali, defaultTheme || LIFE_THEMES_REGISTRY[themeId]);
      const questionId = initialQuestionId && themeQuestions.some((q) => q.id === initialQuestionId)
        ? initialQuestionId
        : (themeQuestions[0]?.id || 'career_timing_promotion_job_change');
      
      setActiveThemeId(themeId);
      setActiveQuestionId(questionId);
      setSearchQuery('');
      loadAnalysis(themeId, questionId);
    }
  }, [isOpen, initialThemeId, initialQuestionId, ageAppropriateThemes, kundali]);

  // Load deterministic analysis result
  const loadAnalysis = (themeId: LifeThemeId, questionId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = runAdvancedLifeAnalysis(kundali, themeId, questionId);
      setCurrentResult(result);

      if (onSelectThemeAndQuestion) {
        onSelectThemeAndQuestion(themeId, questionId);
      }
    } catch (err: any) {
      console.error('Error running life question analysis:', err);
      setError(isHi ? 'हम इस समय यह विश्लेषण पूरा नहीं कर सके।' : "We couldn't complete this reading right now.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTheme = (themeId: LifeThemeId) => {
    setActiveThemeId(themeId);
    const targetTheme = LIFE_THEMES_REGISTRY[themeId];
    const availableQuestions = getDeshKaalPatraQuestions(kundali, targetTheme);
    const firstQ = availableQuestions[0]?.id || targetTheme?.questions[0]?.id || '';
    setActiveQuestionId(firstQ);
    loadAnalysis(themeId, firstQ);
  };

  const handleSelectQuestion = (themeId: LifeThemeId, qId: string) => {
    setActiveThemeId(themeId);
    setActiveQuestionId(qId);
    loadAnalysis(themeId, qId);
  };

  // Keyboard escape handler & click-outside dismissal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Real-time search query matching across all questions in taxonomy
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;

    const matches: { theme: LifeThemeConfig; question: LifeThemeQuestion }[] = [];
    const allThemes = Object.values(LIFE_THEMES_REGISTRY);

    for (const theme of allThemes) {
      for (const q of theme.questions) {
        const textEn = (q.questionEn + ' ' + theme.nameEn + ' ' + q.descriptionEn).toLowerCase();
        const textHi = (q.questionHi + ' ' + theme.nameHi + ' ' + q.descriptionHi).toLowerCase();
        if (textEn.includes(query) || textHi.includes(query)) {
          matches.push({ theme, question: q });
        }
      }
    }
    return matches;
  }, [searchQuery]);

  if (!isOpen) return null;

  const currentTheme = LIFE_THEMES_REGISTRY[activeThemeId] || ageAppropriateThemes[0] || Object.values(LIFE_THEMES_REGISTRY)[0];
  const primaryWindow = currentResult?.phalaBhutaWindows?.primary;
  const natalPromise = currentResult?.natalPromise;
  const promiseTier = natalPromise ? getNatalPromiseTier(natalPromise.score, lang) : null;
  const timingConfluence = primaryWindow ? getTimingConfluence(primaryWindow.confidenceScore, lang) : null;

  const projectedAgeStr = primaryWindow
    ? calculateProjectedAgeWindow(
        kundali.birthDetails?.date || '1990-05-15',
        primaryWindow.startDate,
        primaryWindow.endDate,
        lang
      )
    : '';

  const houseActivationText = primaryWindow?.activatedHouses && primaryWindow.activatedHouses.length > 0
    ? isHi
      ? `संबंधित जीवन क्षेत्रों (भाव ${primaryWindow.activatedHouses.join(', ')}) में अनुकूलता एवं सकारात्मक ऊर्जा`
      : `Harmonious activation of relevant life areas with supportive strength`
    : isHi
      ? 'संबद्ध प्रमुख जीवन क्षेत्रों की अनुकूल स्थिति'
      : 'Harmonious configuration across key relevant life areas';

  const planetarySupportText = currentResult?.karakaAssessments && currentResult.karakaAssessments.length > 0
    ? isHi
      ? `${currentResult.karakaAssessments.map((k) => k.name).slice(0, 2).join(' व ')} जैसे प्रमुख मार्गदर्शक ग्रहों का सकारात्मक सहयोग`
      : `Supportive placement and strength of key guiding planets (${currentResult.karakaAssessments.map((k) => k.name).slice(0, 2).join(', ')})`
    : isHi
      ? 'मार्गदर्शक ग्रहों का सकारात्मक सहयोग'
      : 'Positive supportive guidance from key planetary factors';

  const dashaSupportText = primaryWindow
    ? isHi
      ? `${primaryWindow.mahadashaLord} महादशा एवं ${primaryWindow.antardashaLord} अंतर्दशा का पारस्परिक अनुकूल योग`
      : `Supportive harmony between ${primaryWindow.mahadashaLord} major period and ${primaryWindow.antardashaLord} sub-period`
    : isHi
      ? 'वर्तमान समय का शुभ फल-प्रदाता संबंध'
      : 'Active time periods create a favorable confluence';

  const transitSupportText = currentResult?.gocharAnalysis
    ? isHi
      ? currentResult.gocharAnalysis.doubleTransitActive
        ? 'बृहस्पति एवं शनि का शुभ प्रभाव इस जीवन क्षेत्र को अनुकूलता प्रदान कर रहा है'
        : 'गोचर में प्रमुख ग्रहों का सकारात्मक संचार'
      : currentResult.gocharAnalysis.doubleTransitActive
        ? 'Supportive planetary movements currently bringing favorable focus to this area'
        : 'Favorable planetary movements aligning with key life areas'
    : isHi
      ? 'गोचरीय ग्रहों का सकारात्मक संमिलन'
      : 'Favorable planetary alignment across key areas';

  const handleAskAcharyaEscalation = () => {
    onClose();
    if (onOpenAcharya) {
      onOpenAcharya(searchQuery || (isHi ? currentResult?.themeConfig?.nameHi : currentResult?.themeConfig?.nameEn));
    }
  };

  return (
    <div
      id="basic-life-questions-modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-astro-text/60 backdrop-blur-xs animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="life-questions-modal-title"
    >
      <div
        ref={modalContainerRef}
        id="basic-life-questions-modal-container"
        className="bg-astro-card border border-astro-border rounded-2xl sm:rounded-3xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden text-astro-text"
      >
        {/* Modal Top Header */}
        <div className="bg-astro-bg border-b border-astro-border px-4 sm:px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-astro-primary/10 flex items-center justify-center text-astro-primary border border-astro-primary/20">
              <Compass className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <h2 id="life-questions-modal-title" className="text-base sm:text-lg font-bold text-astro-text font-serif">
                {isHi ? 'जीवन प्रश्न' : 'Life Questions'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'जानिए आपके जन्म विवरण जीवन के महत्वपूर्ण प्रश्नों के बारे में क्या संकेत देते हैं।'
                  : 'Explore what your birth details suggest about key areas of your life.'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-astro-border bg-astro-card hover:bg-astro-border flex items-center justify-center text-astro-secondary-text hover:text-astro-text transition-colors cursor-pointer"
            aria-label={isHi ? 'बंद करें' : 'Close'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          {/* Natural-Language Question Search / Ask Box */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-3.5 sm:p-4 shadow-2xs space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
              <Search className="w-3.5 h-3.5 text-astro-primary" />
              <span>{isHi ? 'कोई भी प्रश्न खोजें या पूछें' : 'Search or Ask Any Question'}</span>
            </div>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  isHi
                    ? 'उदा. पदोन्नति कब होगी? विवाह का योग कब है? विदेश यात्रा... '
                    : 'e.g., When will I get a promotion? Marriage timing? Foreign travel...'
                }
                className="w-full pl-9 pr-8 py-2.5 bg-[#FAF6ED] border border-astro-border focus:border-astro-primary focus:ring-1 focus:ring-astro-primary rounded-xl text-xs sm:text-sm text-astro-text placeholder-[#8C827A] outline-none transition-all"
              />
              <Search className="w-4 h-4 text-[#8C827A] absolute left-3 top-3" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2.5 text-[#8C827A] hover:text-astro-text p-0.5 rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dynamic Search Results Dropdown/List */}
            {searchResults && (
              <div className="pt-2 border-t border-astro-border space-y-1.5 max-h-48 overflow-y-auto feature-nav-scroll">
                {searchResults.length > 0 ? (
                  searchResults.map(({ theme, question }) => (
                    <button
                      key={question.id}
                      onClick={() => {
                        handleSelectQuestion(theme.id, question.id);
                        setSearchQuery('');
                      }}
                      className="w-full text-left p-2.5 rounded-xl hover:bg-[#FAF6ED] border border-transparent hover:border-astro-border text-xs flex items-center justify-between gap-2 transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <span className="w-1.5 h-1.5 rounded-full bg-astro-primary shrink-0" />
                        <span className="font-semibold text-astro-text truncate">
                          {isHi ? question.questionHi : question.questionEn}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-full shrink-0">
                        {isHi ? theme.nameHi.split(',')[0] : theme.nameEn.split('&')[0]}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="p-3 text-center bg-[#FAF6ED] rounded-xl space-y-2">
                    <p className="text-xs text-astro-secondary-text">
                      {isHi
                        ? 'इस प्रश्न के लिए कोई पूर्व-निर्धारित शास्त्रीय सूत्र नहीं मिला। आप सीधे आचार्य से पूछ सकते हैं:'
                        : 'No direct template question matched. You can consult Ask Acharya directly:'}
                    </p>
                    <button
                      onClick={handleAskAcharyaEscalation}
                      className="px-4 py-2 bg-astro-primary hover:bg-astro-secondary text-white rounded-xl text-xs font-bold inline-flex items-center gap-2 shadow-2xs transition-colors cursor-pointer"
                    >
                      <Award className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'आचार्य से पूछें (Ask Acharya)' : 'Consult Ask Acharya'}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 1. Age-Aware Domain Selection Cards */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                {isHi ? 'जीवन क्षेत्र चुनें:' : 'Select Life Area:'}
              </span>
              <span className="text-[11px] text-astro-secondary-text">
                {ageAppropriateThemes.length} {isHi ? 'उपयुक्त क्षेत्र' : 'Relevant Areas'}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {ageAppropriateThemes.map((theme) => {
                const Icon = THEME_ICONS[theme.id] || Sparkles;
                const isSelected = activeThemeId === theme.id;

                return (
                  <button
                    key={theme.id}
                    onClick={() => handleSelectTheme(theme.id)}
                    className={`p-2.5 rounded-xl border text-left text-xs transition-all flex items-center gap-2 min-h-[44px] touch-manipulation cursor-pointer ${
                      isSelected
                        ? 'bg-astro-primary text-white border-astro-primary shadow-2xs ring-2 ring-astro-accent/30 font-bold'
                        : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent hover:bg-[#FAF6ED]'
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                    <span className="truncate">{isHi ? theme.nameHi.split(',')[0] : theme.nameEn.split('&')[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Questions for Selected Category */}
          {(() => {
            const activeQuestions = getDeshKaalPatraQuestions(kundali, currentTheme);
            const themeDisplay = getDeshKaalPatraThemeDisplay(currentTheme, nativeAge, lang);

            return (
              <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-astro-accent" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                      {isHi ? `प्रश्न: ${themeDisplay.name}` : `Questions: ${themeDisplay.name}`}
                    </h3>
                  </div>
                  <span className="text-[11px] text-astro-secondary-text">
                    {activeQuestions.length} {isHi ? 'उपयुक्त प्रश्न' : 'Appropriate Inquiries'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {activeQuestions.map((q) => {
                    const isSelected = activeQuestionId === q.id;

                    return (
                      <button
                        key={q.id}
                        onClick={() => handleSelectQuestion(activeThemeId, q.id)}
                        className={`p-3 rounded-xl border text-left text-xs transition-all flex items-start gap-2.5 min-h-[48px] touch-manipulation cursor-pointer ${
                          isSelected
                            ? 'bg-[#FAF6ED] border-astro-primary shadow-2xs ring-1 ring-astro-primary font-bold text-astro-primary'
                            : 'bg-astro-bg border-astro-border text-astro-text hover:border-astro-accent'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${isSelected ? 'bg-astro-primary' : 'bg-astro-border'}`} />
                        <span className="leading-snug">{isHi ? q.questionHi : q.questionEn}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* 3. Loading State */}
          {isLoading && (
            <div className="bg-astro-bg border border-astro-border rounded-2xl p-8 text-center space-y-3 shadow-2xs">
              <RefreshCw className="w-6 h-6 text-astro-primary animate-spin mx-auto" />
              <p className="text-sm font-semibold text-astro-text">
                {isHi ? 'प्रासंगिक ग्रह स्थितियों का अध्ययन किया जा रहा है…' : 'Reading the relevant factors…'}
              </p>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'जन्म विवरणों के आधार पर विश्लेषण तैयार हो रहा है।' : 'Connecting your birth details to classical timing.'}
              </p>
            </div>
          )}

          {/* 4. Error State */}
          {error && !isLoading && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center space-y-3">
              <AlertCircle className="w-6 h-6 text-rose-600 mx-auto" />
              <p className="text-sm font-bold text-rose-900">{error}</p>
              <button
                onClick={() => loadAnalysis(activeThemeId, activeQuestionId)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>{isHi ? 'पुनः प्रयास करें' : 'Try again'}</span>
              </button>
            </div>
          )}

          {/* 5. Answer-First Reading Presentation */}
          {!isLoading && !error && currentResult && (
            <div className="space-y-5">
              {/* Question Master Principle & Focus Description */}
              {(() => {
                const activeQObj = currentTheme.questions.find((q) => q.id === activeQuestionId) || currentTheme.questions[0];
                if (!activeQObj) return null;
                return (
                  <div className="bg-[#FAF6ED] border border-astro-border rounded-2xl p-4 flex items-start gap-3 shadow-2xs">
                    <div className="p-2 bg-astro-primary/10 rounded-xl text-astro-primary shrink-0 mt-0.5">
                      <HelpCircle className="w-4 h-4 text-astro-accent" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-astro-primary">
                          {isHi ? activeQObj.questionHi : activeQObj.questionEn}
                        </span>
                        <span className="text-[10px] font-bold text-astro-accent bg-astro-accent/10 border border-astro-accent/20 px-2 py-0.5 rounded-full">
                          {activeQObj.category}
                        </span>
                      </div>
                      <p className="text-xs text-astro-secondary-text leading-relaxed">
                        {isHi ? activeQObj.descriptionHi : activeQObj.descriptionEn}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Section 1: What Stands Out & Stronger Period */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left: What Stands Out */}
                <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                        {isHi ? 'मुख्य संकेत' : 'What Stands Out'}
                      </span>
                      {promiseTier && (
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase ${promiseTier.badgeClass}`}>
                          {isHi ? promiseTier.tierHi : promiseTier.tierEn}
                        </span>
                      )}
                    </div>

                    <h4 className="text-base sm:text-lg font-bold text-astro-text leading-snug font-serif mb-2">
                      {isHi ? natalPromise?.verdictHi : natalPromise?.verdictEn}
                    </h4>

                    <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed">
                      {isHi ? natalPromise?.analysisSummaryHi : natalPromise?.analysisSummaryEn}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-astro-border flex items-center justify-between text-xs text-astro-secondary-text">
                    <span>{isHi ? 'शास्त्रीय संदर्भ:' : 'Classical Reference:'}</span>
                    <span className="font-medium text-astro-text text-[11px] truncate max-w-[180px]">
                      {currentTheme.classicalTextReference}
                    </span>
                  </div>
                </div>

                {/* Right: Stronger Period */}
                {primaryWindow && (
                  <div className="lg:col-span-2 bg-astro-bg border-2 border-astro-primary/20 rounded-2xl p-5 shadow-2xs flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-astro-primary text-white rounded-full text-xs font-bold shadow-2xs">
                          <Zap className="w-3.5 h-3.5 text-astro-accent" />
                          <span>{isHi ? 'अनुकूल समय' : 'Your Stronger Period'}</span>
                        </div>

                        {timingConfluence && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#FAF6ED] border border-astro-border text-xs font-bold">
                            <span className="text-astro-accent font-mono">{timingConfluence.stars}</span>
                            <span className={timingConfluence.textColor}>
                              {isHi ? timingConfluence.labelHi : timingConfluence.labelEn}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Date Range Display */}
                      <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 mb-2">
                        <span className="text-xl sm:text-2xl font-black text-astro-text font-mono tracking-tight">
                          {primaryWindow.startDate} — {primaryWindow.endDate}
                        </span>
                        {projectedAgeStr && (
                          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-astro-primary bg-[#FAF6ED] border border-astro-border px-2.5 py-0.5 rounded-md self-start shadow-2xs">
                            <Calendar className="w-3.5 h-3.5 text-astro-primary shrink-0" />
                            <span>{projectedAgeStr}</span>
                          </span>
                        )}
                      </div>

                      <p className="text-xs sm:text-sm text-astro-text leading-relaxed font-serif">
                        {isHi ? primaryWindow.astrologicalMechanismHi : primaryWindow.astrologicalMechanismEn}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-astro-border flex flex-wrap items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-2 text-astro-secondary-text">
                        <Calendar className="w-3.5 h-3.5 text-astro-primary" />
                        <span>{isHi ? 'सक्रिय अनुकूल काल:' : 'Active Supportive Period:'}</span>
                        <span className="font-bold font-mono text-astro-primary">
                          {primaryWindow.mahadashaLord}–{primaryWindow.antardashaLord}
                        </span>
                      </div>
                      <span className="text-[11px] text-astro-secondary-text">
                        {isHi ? 'अनुकूल ग्रह संयोग' : 'Favourable Timing Alignment'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 2: Why This Time Matters */}
              <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-2xs space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2.5">
                  <CheckCircle2 className="w-4 h-4 text-astro-primary" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                    {isHi ? 'यह समय क्यों महत्वपूर्ण है' : 'Why This Time Matters'}
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-3.5 rounded-xl bg-[#FAF6ED] border border-astro-border flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-astro-primary/15 text-astro-primary font-bold font-mono text-xs flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <span className="text-[11px] font-bold text-astro-secondary-text uppercase">
                        {isHi ? 'सक्रिय जीवन क्षेत्र' : 'Supportive Areas'}
                      </span>
                      <p className="text-xs text-astro-text mt-0.5 leading-relaxed">{houseActivationText}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-[#FAF6ED] border border-astro-border flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-astro-primary/15 text-astro-primary font-bold font-mono text-xs flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <span className="text-[11px] font-bold text-astro-secondary-text uppercase">
                        {isHi ? 'ग्रहीय सहयोग' : 'Planetary Support'}
                      </span>
                      <p className="text-xs text-astro-text mt-0.5 leading-relaxed">{planetarySupportText}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-[#FAF6ED] border border-astro-border flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-astro-primary/15 text-astro-primary font-bold font-mono text-xs flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <span className="text-[11px] font-bold text-astro-secondary-text uppercase">
                        {isHi ? 'वर्तमान दशा प्रभाव' : 'Active Period Alignment'}
                      </span>
                      <p className="text-xs text-astro-text mt-0.5 leading-relaxed">{dashaSupportText}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-[#FAF6ED] border border-astro-border flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-astro-primary/15 text-astro-primary font-bold font-mono text-xs flex items-center justify-center shrink-0 mt-0.5">
                      4
                    </span>
                    <div>
                      <span className="text-[11px] font-bold text-astro-secondary-text uppercase">
                        {isHi ? 'गोचरीय ग्रहों का संमिलन' : 'Planetary Movement Support'}
                      </span>
                      <p className="text-xs text-astro-text mt-0.5 leading-relaxed">{transitSupportText}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: What to Keep in Mind */}
              <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-2xs space-y-2.5">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Sparkles className="w-4 h-4 text-astro-accent" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-astro-text">
                    {isHi ? 'व्यावहारिक सुझाव एवं ध्यान देने योग्य बातें' : 'What to Keep in Mind'}
                  </h4>
                </div>

                <p className="text-xs sm:text-sm text-astro-secondary-text leading-relaxed">
                  {isHi ? currentResult.overallFeedbackHi : currentResult.overallFeedbackEn}
                </p>

                {currentResult.remedies && currentResult.remedies.length > 0 && (
                  <div className="pt-2 flex flex-wrap gap-2">
                    {currentResult.remedies.slice(0, 2).map((rem, idx) => (
                      <div key={idx} className="px-3 py-1.5 rounded-lg bg-[#FAF6ED] border border-astro-border text-xs text-astro-text">
                        <span className="font-bold text-astro-primary mr-1.5">[{rem.type}]:</span>
                        <span>{isHi ? rem.titleHi : rem.titleEn}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls with Ask Acharya Escalation */}
        <div className="bg-astro-bg border-t border-astro-border px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-astro-secondary-text text-center sm:text-left">
            {isHi ? 'शास्त्रीय फलित ज्योतिष आधारित स्पष्ट मार्गदर्शन' : 'Clear Jyotish guidance grounded in classical principles'}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto flex-wrap">
            {/* Ask Acharya Escalation CTA */}
            {onOpenAcharya && (
              <button
                onClick={handleAskAcharyaEscalation}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-astro-accent hover:bg-[#99733D] text-white text-xs font-bold flex items-center justify-center gap-2 transition-colors min-h-[44px] touch-manipulation shadow-2xs cursor-pointer"
              >
                <Award className="w-4 h-4 text-white" />
                <span>{isHi ? 'आचार्य से पूछें' : 'Ask Acharya'}</span>
              </button>
            )}

            {onOpenDetailedEvidence && (
              <button
                onClick={() => {
                  onClose();
                  onOpenDetailedEvidence();
                }}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-astro-primary bg-astro-bg hover:bg-[#FAF6ED] text-astro-primary text-xs font-bold flex items-center justify-center gap-2 transition-colors min-h-[44px] touch-manipulation cursor-pointer"
              >
                <BookOpen className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'विस्तृत कारण समझें' : 'Understand Reasons'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              onClick={onClose}
              className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold transition-colors min-h-[44px] touch-manipulation shadow-2xs cursor-pointer"
            >
              {isHi ? 'वापस जाएं' : 'Return'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
